import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import {
  SearchSelectOption,
  SearchSelect,
} from '@app/components/core/input/searchSelect/SearchSelect'
import { useClient } from '@based/sdk/react'
import { Style } from 'inlines'

export type LocalitySelectorProps = {
  label?: string
  labelStyle?: Style
  displayLabelAsterisk?: boolean
  locality: SearchSelectOption
  filterByRegionId?: number
  setLocality: (locality: SearchSelectOption) => void
}

export const LocalitySelector = ({
  label,
  labelStyle,
  displayLabelAsterisk,
  locality,
  filterByRegionId,
  setLocality,
}: LocalitySelectorProps) => {
  const translations = useTranslations().app.common

  const [localityQuery, setLocalityQuery] = useState(locality.value)
  const [localityOptions, setLocalityOptions] = useState<SearchSelectOption[]>(
    [],
  )
  const [renderNotFoundMessage, setRenderNotFoundMessage] = useState(false)

  const selectLocationOption = useCallback(
    (option: SearchSelectOption) => {
      setLocalityQuery(option.value)
      setLocality(option)
      setLocalityOptions([])
      setRenderNotFoundMessage(false)
    },
    [setLocality],
  )

  const client = useClient()
  useEffect(() => {
    if (localityQuery === locality.value) {
      return
    }
    const close = client
      .query('suggestions-locality', {
        searchTerm: localityQuery,
        regionId: filterByRegionId ?? null,
        top: 7,
      })
      .subscribe((r) => {
        const result: SearchSelectOption[] = r.map(
          (m: { id: any; name: any }) => ({
            key: m.id,
            value: m.name,
          }),
        )
        const exactMatch = result.find(
          (r) => r.value.toLowerCase() === localityQuery.toLowerCase(),
        )
        if (exactMatch) {
          if (
            exactMatch.key === locality.key &&
            exactMatch.value === locality.value
          ) {
            setLocalityOptions([])
            setRenderNotFoundMessage(false)
          } else {
            selectLocationOption(exactMatch)
          }
        } else if (result.length === 0) {
          setLocalityOptions([])
          setRenderNotFoundMessage(true)
        } else {
          setLocalityOptions(result)
          setRenderNotFoundMessage(false)
        }
      })
    return () => close()
  }, [client, localityQuery, locality, filterByRegionId, selectLocationOption])

  useEffect(() => {
    setLocalityQuery(locality.value)
    setLocalityOptions([])
  }, [locality])

  const resetLocationSelection = useCallback(() => {
    setLocalityQuery(locality.value)
    setLocalityOptions([])
  }, [locality])

  return (
    <SearchSelect
      label={label ?? translations.location}
      labelStyle={labelStyle}
      displayLabelAsterisk={displayLabelAsterisk}
      value={localityQuery}
      setValue={setLocalityQuery}
      selectOption={selectLocationOption}
      abandonSelection={resetLocationSelection}
      options={localityOptions}
      noResultsMessage={{
        display: renderNotFoundMessage,
        message: translations.locationNotFoundMessage,
      }}
    />
  )
}
